import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getGroupsForUserRequest,
  updateUserNotificationGroupsRequest,
  updateUserRequest,
} from "../../../../../actions";
import { UserGroupDescription, UserGroupLabel, UserGroups } from "../../../../../enums/user-groups";
import { IRootReducer, UserInfoAdmin } from "../../../../../reducers";
import {
  CleanPhoneNumberUtil,
  ParsePhoneNumberUtil,
  ValidatePhoneNumberUtil,
} from "../../../../../utils/string-utils";
import { getAllNotificationGroupsRequest, getUserNotificationPrefsRequest } from "../../../../../actions";
import { useAuthContext } from "../../../../../core/auth-context";
import { AgencyId, GetAgency, getAgencyId, JogId } from "../../../../../reducers/states/agency";
import { NotificationGroup } from "../../../../../reducers/states/user-info";
import ViewEditSelect from "../../../../common/view-edit-select";
import ViewEditAgencyAutocomplete from "../../../../common/view-edit-agency-autocomplete";
import { getJogProps } from "../invite-user";
import { useJogs } from "../../../../../hooks/jogs";

interface EditUserProps {
  props: {
    rowCountSelected: number;
    userSelection: UserInfoAdmin;
  };
}

const EditUser: FC<EditUserProps> = ({ props }) => {
  // Form Input
  const [userDetails, setUserDetails] = useState({} as UserInfoAdmin);
  const [userRoles, setUserRoles] = useState([] as string[]);

  // User Preferences
  const [userNotifGroups, setUserNotifGroups] = useState([] as number[])

  // Form Controls
  const [allNotifGroups, setAllNotifGroups] = useState([] as NotificationGroup[]);
  const [open, setOpen] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [agencyReadonly, setAgencyReadOnly] = useState(false);
  const [jogReadonly, setJogReadOnly] = useState(true);
  const previousJogIdRef = useRef("");

  const dispatch = useDispatch();
  const userInfoStore = useSelector((state: IRootReducer) => state.userInfoReducer);
  const initialGroups = useSelector((state: IRootReducer) => state.adminReducer.groupsForUser);
  const agenciesById = useSelector((state: IRootReducer) => state.agencyReducer.agenciesById);

  const { authState } = useAuthContext();

  const jogs = useJogs();
  const userAgency = GetAgency(agenciesById, userDetails.agency); // todo: refactor to use useAgencies.get(), etc, instead of using agencyStore and GetAgency
  const userAgencyJogs = jogs.getAgencyJogs(userAgency);

  useEffect(() => {
    // Disable save if phone number is invalid. Blank is ok.
    if (userDetails.phoneNumber !== ""
      && !ValidatePhoneNumberUtil(userDetails.phoneNumber)) {
      setSaveDisabled(true);
      return;
    }

    // Disable save if any required field is blank
    setSaveDisabled(
      userDetails.firstName === "" ||
      userDetails.lastName === "" ||
      userDetails.agency === "" ||
      userDetails.jogId === JogId.NOT_SELECTED || userDetails.jogId === JogId.Other
    );
  }, [
    userDetails.agency,
    userDetails.firstName,
    userDetails.lastName,
    userDetails.phoneNumber,
    userDetails.jogId,
  ]);

  useEffect(() => {
    setUserRoles(initialGroups);
  }, [initialGroups]);


  useEffect(() => {
    if (userInfoStore.userNotificationPrefs) {
      setUserNotifGroups(userInfoStore.userNotificationPrefs.groupIds)
    }
  }, [userInfoStore.userNotificationPrefs]);


  useEffect(() => {
    if (userInfoStore.allNotificationGroups) {
      setAllNotifGroups(userInfoStore.allNotificationGroups)
    }
  }, [userInfoStore.allNotificationGroups]);


  const handleOpen = () => {
    dispatch(getGroupsForUserRequest(props.userSelection?.email));
    dispatch(getUserNotificationPrefsRequest(props.userSelection.email));
    dispatch(getAllNotificationGroupsRequest());

    setUserDetails(props.userSelection);
    previousJogIdRef.current = props.userSelection.jogId || "";

    if (authState.userGroups.toString().includes(UserGroups.AGENCY_ADMIN)
      && !authState.userGroups.toString().includes(UserGroups.SITE_ADMIN)) {
      setAgencyReadOnly(true);
    }

    // Handles two cases: 1) Ensure JOG is editible for agencies in multiple JOGs. 2) Ensure JOG is editible if it has not been set.
    const { isReadOnly } = getJogProps(GetAgency(agenciesById, props.userSelection.agency));
    const isUserJogSet = props.userSelection.jogId !== "" && props.userSelection.jogId !== JogId.Other;
    setJogReadOnly(isReadOnly && isUserJogSet);

    setOpen(true);
  };

  const handleInputChange = (e: any, field: string) => {
    let value: string = e as string;
    setUserDetails((current) => ({ ...current, [field]: value }));
  };

  const handleAgencySelectionChange = (agencyId: AgencyId | null, elementId: string) => {
    const id = getAgencyId(agencyId || "");
    const { agencyJogId, isReadOnly } = getJogProps(agenciesById[id]);

    setJogReadOnly(isReadOnly);
    setUserDetails((current) => ({
      ...current,
      agency: agencyId as string,
      jogId: agencyJogId,
    }));
  };

  const handleJogSelectionChange = (jogId: JogId, elementId: string) => {
    setUserDetails((current) => ({ ...current, jogId: jogId }));
  };

  const handleCancel = () => {
    setOpen(false);
    setAgencyReadOnly(false);
    setJogReadOnly(true);
    setUserDetails({} as UserInfoAdmin);
    setUserRoles([]);
  };

  const handleSave = () => {
    let cleanNumber = "";
    if (userDetails.phoneNumber) {
      cleanNumber = CleanPhoneNumberUtil(userDetails.phoneNumber);
    }

    let rolesToRemove = initialGroups.filter((r) => !userRoles.includes(r));
    let rolesToAdd = userRoles.filter((r) => !initialGroups.includes(r));

    dispatch(
      updateUserRequest(
        (authState.email ?? "").toLowerCase(),
        userDetails.cognitoUsername,
        userDetails.email,
        userDetails.firstName,
        userDetails.lastName,
        cleanNumber,
        userDetails.agency || "",
        userDetails.jogId || "",
        userDetails.enabled,
        userRoles,
        rolesToAdd,
        rolesToRemove,
      )
    );

    if (userDetails.jogId !== previousJogIdRef.current) {
      const prevJogName = jogs.getName(previousJogIdRef.current)?.toLowerCase();
      const newJogName = jogs.getName(userDetails.jogId)?.toLowerCase();
      
      // allNotifGroups is objects with *notification group IDs* (not JOG IDs), so we're finding matches by name.
      const idToRemove = Number(allNotifGroups.find(g => g.name.toLowerCase() === prevJogName)?.id ?? 0);
      const idToAdd = Number(allNotifGroups.find(g => g.name.toLowerCase() === newJogName)?.id ?? 0);

      let newNotifGroups = idToRemove
        ? userNotifGroups.filter(id => id !== idToRemove)
        : [...userNotifGroups];

      if (idToAdd) {
        newNotifGroups.push(idToAdd);
      }

      setUserNotifGroups(newNotifGroups);
      dispatch(updateUserNotificationGroupsRequest(userDetails.email, newNotifGroups));
    }

    setOpen(false);
  };

  const handleRoleSelect = (event: ChangeEvent<HTMLInputElement>) => {
    let newRoles = [...userRoles];
    if (
      event.target.checked === true &&
      newRoles.indexOf(event.target.id) === -1
    ) {
      newRoles.push(event.target.id);
    } else {
      newRoles = newRoles.filter((r) => r !== event.target.id);
    }

    setUserRoles(newRoles);
  };

  const isBtnEnabled = () => {
    return props.rowCountSelected === 1;
  }

  return (
    <>
      <Tooltip title="Edit a user in the VCC">
        {/* Wrapped in "Box" to avoid MUI console error for wrapping disabled element in Tooltip */}
        <Box>
          <Button
            disabled={!isBtnEnabled()}
            color={isBtnEnabled() ? "primary" : "inherit"}
            onClick={handleOpen}
            style={{ minWidth: "150px", color: isBtnEnabled() ? "white" : "black" }}
            variant="contained"
          >
            Edit User
          </Button>
        </Box>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleCancel}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Edit User</DialogTitle>
        <DialogContent className="invite-edit-user">
          <DialogContentText>
            Update user information and roles in the VCC
          </DialogContentText>
          <TextField
            autoFocus
            required
            variant="outlined"
            margin="dense"
            id="email"
            label="Email Address"
            type="email"
            fullWidth
            disabled
            value={userDetails.email}
          />
          <TextField
            required
            variant="outlined"
            margin="dense"
            id="firstName"
            label="First Name"
            type="string"
            fullWidth
            value={userDetails.firstName}
            onChange={(e) => handleInputChange(e.target.value, e.target.id)}
          />
          <TextField
            required
            variant="outlined"
            margin="dense"
            id="lastName"
            label="Last Name"
            type="string"
            fullWidth
            value={userDetails.lastName}
            onChange={(e) => handleInputChange(e.target.value, e.target.id)}
          />
          <TextField
            variant="outlined"
            margin="dense"
            id="phoneNumber"
            label="Phone Number"
            type="phoneNumber"
            fullWidth
            value={ParsePhoneNumberUtil(userDetails.phoneNumber)}
            onChange={(e) => handleInputChange(e.target.value, e.target.id)}
          />
          <ViewEditAgencyAutocomplete
            isEdit={!agencyReadonly}
            required={true}
            size="medium"
            value={userDetails.agency || ""}
            enableOther={false}
            handleInputChange={handleAgencySelectionChange}
          />
          <ViewEditSelect
            id="jogId"
            label="Primary JOG"
            value={userDetails.jogId !== JogId.Other ? userDetails.jogId : undefined}
            isEdit={!jogReadonly}
            required={true}
            size="medium"
            tooltip="User's primary JOG. Users will receive notifications for incidents created in primary JOG."
            handleInputChange={handleJogSelectionChange}
          >
            {userAgencyJogs.map(
              jog => <MenuItem key={jog.id} value={jog.id}>{jog.name}</MenuItem>
            )}
          </ViewEditSelect>
          <Divider sx={{ margin: "1em 0" }} />
          <FormGroup sx={{ margin: "10px" }}>
            <DialogContentText>
              User Roles
            </DialogContentText>
            <Grid container>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox checked={true} id={UserGroups.VCC_BASIC} />
                  }
                  label={UserGroupLabel.VCC_BASIC}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.VCC_BASIC}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        userRoles.indexOf(UserGroups.INC_CONTRIBUTOR) > -1
                      }
                      id={UserGroups.INC_CONTRIBUTOR}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.INC_CONTRIBUTOR}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.INC_CONTRIBUTOR}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        userRoles.indexOf(UserGroups.INC_MANAGER) > -1
                      }
                      id={UserGroups.INC_MANAGER}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.INC_MANAGER}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.INC_MANAGER}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        userRoles.indexOf(UserGroups.INC_RECORDS_MANAGER) >
                        -1
                      }
                      id={UserGroups.INC_RECORDS_MANAGER}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.INC_RECORDS_MANAGER}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.INC_RECORDS_MANAGER}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={userRoles.indexOf(UserGroups.PIO) > -1}
                      id={UserGroups.PIO}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.PIO}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.PIO}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={userRoles.indexOf(UserGroups.MAJOR_EVENT_CONTRIBUTOR) > -1}
                      id={UserGroups.MAJOR_EVENT_CONTRIBUTOR}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.MAJOR_EVENT_CONTRIBUTOR}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.MAJOR_EVENT_CONTRIBUTOR}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={userRoles.indexOf(UserGroups.AGENCY_ADMIN) > -1}
                      id={UserGroups.AGENCY_ADMIN}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.AGENCY_ADMIN}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.AGENCY_ADMIN}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                {(authState.userGroups.indexOf(UserGroups.SITE_ADMIN) > -1) && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userRoles.indexOf(UserGroups.SITE_ADMIN) > -1}
                        id={UserGroups.SITE_ADMIN}
                        onChange={handleRoleSelect}
                      />
                    }
                    label={UserGroupLabel.SITE_ADMIN}
                  />)}
              </Grid>
            </Grid>
          </FormGroup>
          <DialogActions>
            <Button onClick={handleCancel} data-rum-id="user-cancel" color="inherit" variant="contained">
              Cancel
            </Button>
            <Button
              disabled={saveDisabled}
              onClick={handleSave}
              color="primary"
              variant="contained"
            >
              Save
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditUser;
